import { useEffect, useMemo, useState } from "react";
import { api, AMENITY_LABELS } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import ListingsMap from "@/components/ListingsMap";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { MagnifyingGlass, SquaresFour, MapPin } from "@phosphor-icons/react";

export default function Browse() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [q, setQ] = useState("");
  const [nb, setNb] = useState("all");
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [view, setView] = useState("grid"); // grid | map

  useEffect(() => {
    api.get("/listings/neighborhoods").then((r) => setNeighborhoods(r.data));
    api.get("/listings/amenities").then((r) => setAmenities(r.data));
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      const params = {};
      if (q) params.q = q;
      if (nb !== "all") params.neighborhood = nb;
      if (selectedAmenities.length) params.amenities = selectedAmenities.join(",");
      const { data } = await api.get("/listings", { params });
      setListings(data);
      setLoading(false);
    };
    const t = setTimeout(run, 200);
    return () => clearTimeout(t);
  }, [q, nb, selectedAmenities]);

  const toggleAmenity = (a) =>
    setSelectedAmenities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const emptyState = !loading && listings.length === 0;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-neutral-200 pb-8">
        <div>
          <div className="label-eyebrow mb-3">All dens</div>
          <h1 className="font-display font-medium text-4xl sm:text-5xl tracking-tight">Find a den to work from</h1>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-80">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search neighborhoods, titles..."
              className="pl-9 h-11 border-neutral-300 focus-visible:ring-neutral-900"
            />
          </div>
          <Select value={nb} onValueChange={setNb}>
            <SelectTrigger data-testid="neighborhood-filter" className="h-11 w-48 border-neutral-300">
              <SelectValue placeholder="Neighborhood" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All neighborhoods</SelectItem>
              {neighborhoods.map((n) => (
                <SelectItem key={n} value={n}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border border-neutral-300 h-11">
            <button
              data-testid="view-grid-btn"
              onClick={() => setView("grid")}
              className={`px-3 flex items-center gap-1.5 text-xs transition-colors ${view === "grid" ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-50"}`}
            ><SquaresFour size={14}/> Grid</button>
            <button
              data-testid="view-map-btn"
              onClick={() => setView("map")}
              className={`px-3 flex items-center gap-1.5 text-xs border-l border-neutral-300 transition-colors ${view === "map" ? "bg-neutral-900 text-white border-l-neutral-900" : "text-neutral-700 hover:bg-neutral-50"}`}
            ><MapPin size={14}/> Map</button>
          </div>
        </div>
      </div>

      {view === "map" ? (
        loading ? <Skeleton className="h-[70vh] w-full"/> : <ListingsMap listings={listings}/>
      ) : (
      <div className="grid lg:grid-cols-12 gap-10">
        {/* Filter sidebar */}
        <aside className="lg:col-span-3">
          <div className="sticky top-24">
            <div className="label-eyebrow mb-4">Amenities</div>
            <div className="space-y-3">
              {amenities.map((a) => (
                <label key={a} className="flex items-center gap-3 text-sm cursor-pointer group">
                  <Checkbox
                    data-testid={`amenity-${a}`}
                    checked={selectedAmenities.includes(a)}
                    onCheckedChange={() => toggleAmenity(a)}
                    className="border-neutral-400 data-[state=checked]:bg-neutral-900 data-[state=checked]:border-neutral-900"
                  />
                  <span className="text-neutral-700 group-hover:text-neutral-900 transition-colors">
                    {AMENITY_LABELS[a] || a}
                  </span>
                </label>
              ))}
            </div>
            {selectedAmenities.length > 0 && (
              <button
                data-testid="clear-filters"
                className="mt-6 text-xs underline text-neutral-500 hover:text-neutral-900"
                onClick={() => setSelectedAmenities([])}
              >Clear filters</button>
            )}
          </div>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-9">
          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="border border-neutral-200">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <div className="p-5 space-y-3">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : emptyState ? (
            <div className="border border-neutral-200 p-16 text-center">
              <div className="label-eyebrow mb-3">No results</div>
              <p className="text-neutral-600">Try loosening your filters or a different neighborhood.</p>
            </div>
          ) : (
            <div data-testid="listing-grid" className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
